<template>
	<div>
		<form
			@submit.prevent="signIn()"
			class="space-y-6">
			<div>
				<tw-input
					v-model="email"
					id="email"
					name="email"
					:required="true"
					type="email"
					autocomplete="email"
					label="Email address"
					validation="Please enter your email address." />
			</div>

			<div class="relative">
				<tw-input
					v-model="password"
					id="password"
					name="password"
					:required="true"
					type="password"
					autocomplete="current-password"
					label="Password"
					validation="Please enter your password." />
				<div class="text-sm absolute top-0 right-0">
					<router-link
						:to="{name: 'ForgotPassword'}"
						class="link">
						Forgot your password?
					</router-link>
				</div>
			</div>
			<div>
				<div
					v-if="message"
					class="text-red-600 w-full justify-center text-sm font-bold pb-4 items-end flex">
					{{ message }}
				</div>
				<div class="pt-4">
					<tw-button
						type="submit"
						class="btn-db-yellow w-full"
						:disabled="loading"
						:loading="loading">
						Log In
					</tw-button>
				</div>
			</div>
		</form>
	</div>
</template>

<script lang="ts">
	export default { name: 'SignInForm' } // name the component
</script>

<script setup lang="ts">
	// api
	import { 
		Auth
	} from 'aws-amplify'

	// router
	import {
		useRouter,
		useRoute
	} from 'vue-router'

	// store
	import { 
		store as _systemStore
	} from '@/store/system'
	
	import { 
		store as _userStore
	} from '@/store/user'

	// types
	import { 
		CognitoUser
	} from '@aws-amplify/auth'
	
	// vue
	import {
		ref
	} from 'vue'

	const emit = defineEmits([
		'passwordExpired'
	])

	const route = useRoute()
	const router = useRouter()
	
	const userStore = _userStore()
	const systemStore = _systemStore()

	// #region Sign in form
	const email = ref("")
	const password = ref("")
	const loading = ref(false)
	const signInError = ref(false)
	const user = ref({} as CognitoUser)
	const message = ref<string>('')

	async function signIn() {
		try {
			loading.value = true
			message.value = ""

			// check for empty field values
			if (email.value == "") {
				document.getElementById('email')?.focus()
				signInError.value = true
				throw 'Empty email'
			}
			else if (password.value == "") {
				document.getElementById('password')?.focus()
				signInError.value = true
				throw 'Empty password'
			}
		
			// attempt sign in
			user.value = await Auth.signIn(email.value, password.value)
			userStore.user.cognitoUser = user.value

			if(user.value?.challengeName === "NEW_PASSWORD_REQUIRED") { // go to update one time password form
				loading.value = false
				emit('passwordExpired')
			}
			else {			  
				loading.value = false
				router.push(route?.query?.redirect ? String(route?.query?.redirect) : { name: systemStore.system.primaryRoute})
			}
		}
		catch(error:any) {
			if(String(error).includes('PasswordResetRequiredException')) { // password was reset through Cognito console
				router.push({name: 'ResetPasswordRequested', state: {email: email.value}})
			}
			else if(String(error).includes('UserNotConfirmedException')) {
				// resendConfirmationCode()
				// router.push({name: 'AccountConfirmation', params: {email: email.value}})
			}
			else if(String(error).includes('Incorrect username or password')) {
				message.value = "Invalid email or password."
			}
			else if(String(error).includes('UserNotFoundException')) {
				message.value = 'Invalid email or password.'
			}
			else if(String(error).includes('Password attempts exceeded')) {
				message.value = "Password attempts exceeded. Please try again later or reset your password using the 'Forgot your password?' button."
			}
			else if(String(error).includes('Empty password') || String(error).includes('Empty email')) {
				return
			}
			else {
				message.value = "There was an issue with logging into your account. Please try again or contact us for assistance."
			}
		}
		finally {
			loading.value = false
		}
	}
  // #endregion
</script>
